// src/components/presence/presenceSource.js
// Backing-source adapters for the Presence model.
//
// A Presence is one entity that renders through a single panel (PresencePanel) at
// increasing levels of richness. Its backing source is a SWITCH, not a fork: today the
// only source is a directory listing; a community source (via directory_listings.
// claimed_by_group_id) and an advert source are added later as sibling adapters feeding
// the SAME normalised shape. Do not branch PresencePanel on source type — add an adapter here.
//
// Normalised presence source:
//   {
//     image:       string | null,                       // null → PresencePanel shows an initial placeholder
//     name:        string,
//     description: string | null,
//     contacts:    Array<{ type: 'phone'|'email'|'website'|'address', value: string }>,
//     tags:        string[],
//     backing:     { type: 'listing'|'community'|'advert', id, groupId?: string|null }
//   }
//
// backing.groupId is set ONLY when the source resolves to a verified community — it drives
// the "View group" affordance. For a listing that is `claim_status = 'verified'` with a
// claimed_by_group_id, that column becomes groupId (the existing directory→group link).

// A directory_listings row → normalised presence source.
export function listingToPresence(listing) {
  if (!listing) return null;
  const contacts = [
    listing.phone && { type: 'phone', value: listing.phone },
    listing.email && { type: 'email', value: listing.email },
    listing.website && { type: 'website', value: listing.website },
    listing.address && { type: 'address', value: listing.address }
  ].filter(Boolean);

  return {
    image: null, // no per-listing image column yet — placeholder path (image-optional v1)
    name: listing.name,
    description: listing.description || null,
    contacts,
    tags: listing.tags || [],
    backing: {
      type: 'listing',
      id: listing.id,
      // Only a verified claim surfaces the group link — matches the directory→group affordance.
      groupId: listing.claim_status === 'verified' ? (listing.claimed_by_group_id || null) : null
    }
  };
}

// An official-contact view object (as mapped by useDirectoryListings: { id, name, phone,
// icon, languages, groupId, email, website, address }) → normalised presence source.
// `languages` is the contact's description column; `groupId` is already verified-only from the
// hook mapping. contacts[] mirrors listingToPresence's field order and filter convention exactly
// (16 Aug fix — this previously mapped phone only, silently dropping email/website/address even
// though mapOfficialContacts already carries all three on the object).
export function contactToPresence(contact) {
  if (!contact) return null;
  const contacts = [
    contact.phone && { type: 'phone', value: contact.phone },
    contact.email && { type: 'email', value: contact.email },
    contact.website && { type: 'website', value: contact.website },
    contact.address && { type: 'address', value: contact.address }
  ].filter(Boolean);

  return {
    image: null,
    name: contact.name,
    description: contact.languages || null,
    contacts,
    tags: [],
    backing: {
      type: 'listing',
      id: contact.id,
      groupId: contact.groupId || null
    }
  };
}
